import { memo } from 'react'
import type {
	Background,
	BorderRadiusCorners,
	Dimension,
	ShapeFillMode,
	ShapeFit,
	ShapeKind,
	ShapePreset,
	Spacing,
	TextColor,
} from '../data/v1'
import { borderRadiusStyle } from '../lib/border-radius'
import { SHAPE_DEFAULT_FILL, SHAPE_PRESETS } from '../lib/shape-presets'
import { useResolvedColor } from '../lib/use-resolved-color'
import { BackgroundFill } from './background-fill'

export type ShapeLayerRendererProps = {
	layerId: string
	shapeKind: ShapeKind
	preset: ShapePreset
	fillMode: ShapeFillMode
	fill: TextColor
	shapeFit: ShapeFit
	width: Dimension
	height: Dimension
	background: Background
	borderRadius: number
	borderRadiusCorners: BorderRadiusCorners
	spacing: Spacing
}

function resolveDimension(dim: Dimension, canvasVar: string): string {
	return dim.mode === '%'
		? `calc(var(${canvasVar}) * ${dim.percentValue / 100})`
		: `${dim.pxValue}px`
}

function PresetGraphic({ preset, fit, fill }: { preset: ShapePreset; fit: string; fill?: string }) {
	return (
		<svg viewBox={SHAPE_PRESETS[preset].viewBox} preserveAspectRatio={fit} className="absolute inset-0 size-full">
			<path d={SHAPE_PRESETS[preset].path} fill={fill} fillRule="evenodd" />
		</svg>
	)
}

export const ShapeLayerRenderer = memo(function ShapeLayerRenderer(
	{
		layerId,
		shapeKind,
		preset,
		fillMode,
		fill,
		shapeFit,
		width,
		height,
		background,
		borderRadius,
		borderRadiusCorners,
		spacing,
	}: ShapeLayerRendererProps,
) {
	const resolvedFill = useResolvedColor(fill)
	const fit = shapeFit === 'stretch' ? 'none' : 'xMidYMid meet'
	const overridesFill = fillMode === 'value'

	const paintColor = (overridesFill ? resolvedFill : undefined) ?? SHAPE_DEFAULT_FILL

	return (
		<div
			data-layer-id={layerId}
			className="relative overflow-hidden"
			style={{
				pointerEvents: 'auto',
				// width/height size the drawn shape; padding grows the box around it, the way it
				// does on every other layer. `BackgroundFill` is absolutely positioned, so it
				// covers the padding box and the background grows with it.
				boxSizing: 'content-box',
				width: resolveDimension(width, '--canvas-width'),
				height: resolveDimension(height, '--canvas-height'),
				...borderRadiusStyle(borderRadius, borderRadiusCorners),
				paddingTop: spacing.paddingTop,
				paddingBottom: spacing.paddingBottom,
				paddingLeft: spacing.paddingLeft,
				paddingRight: spacing.paddingRight,
				marginTop: spacing.marginTop,
				marginBottom: spacing.marginBottom,
				marginLeft: spacing.marginLeft,
				marginRight: spacing.marginRight,
			}}
		>
			<BackgroundFill background={background} />
			{(shapeKind !== 'rectangle' || overridesFill) && (
				// Must stay in flow: an absolutely positioned child sizes to the padding box and
				// so ignores padding, while `size-full` here resolves against the content box.
				<div className="relative size-full">
					{shapeKind === 'rectangle' && (
						<div className="absolute inset-0" style={{ backgroundColor: paintColor }} />
					)}
					{shapeKind === 'preset' && (
						<PresetGraphic
							preset={preset}
							fit={fit}
							fill={paintColor}
						/>
					)}
				</div>
			)}
		</div>
	)
})
